// Contexto de empresa -- fonte única da "empresa selecionada" para toda a
// app pós-login (ver plano em C:\Users\henri\.claude\plans\vivid-mapping-metcalfe.md).
// Antes disto, cada página tinha o seu próprio useState/useEffect a tentar
// seguir a empresa escolhida no Dashboard, e várias divergiam ou nem
// recebiam o valor (ex: ModalExtrato). useEmpresa() substitui tudo isso.
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { apiGet } from "../lib/api";

const EmpresaCtx = createContext(null);

// Só para PRÉ-realçar a última empresa usada no ecrã de escolha -- nunca
// para saltar esse ecrã silenciosamente (é obrigatório em cada sessão NOVA).
const LAST_EMPRESA_KEY = "empresa_last_selected_v1";

// sessionStorage (não localStorage): dura só a vida do separador -- permite
// que um REFRESH dentro da mesma sessão já ativa seja impercetível (não
// mostra o ecrã de escolha outra vez), sem violar "obrigatório em cada
// sessão": um separador ou login novo começa sempre sem nada guardado aqui.
const SESSION_EMPRESA_KEY = "selected_empresa_nif";

export function lerUltimaEmpresa() {
  try {
    return localStorage.getItem(LAST_EMPRESA_KEY) || "";
  } catch {
    return "";
  }
}

// Root usa isto para decidir se salta o ecrã de escolha -- ANTES de a lista
// real de empresas estar carregada, por isso só confirma "já houve uma
// escolha nesta sessão" (null = nunca, "" = escolheu "Todas as Empresas"
// explicitamente, string = NIF). A validação a sério (o NIF ainda existe?)
// só é possível depois de /api/empresas responder -- ver o useEffect de
// integridade abaixo, em EmpresaProvider.
export function temEmpresaGuardadaNaSessao() {
  try {
    return !!sessionStorage.getItem(SESSION_EMPRESA_KEY);
  } catch {
    return false;
  }
}

export function EmpresaProvider({ children }) {
  const [empresa, setEmpresaState] = useState(() => {
    try {
      const guardada = sessionStorage.getItem(SESSION_EMPRESA_KEY);
      return guardada === null ? "" : guardada;
    } catch {
      return "";
    }
  });
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);

  // Única chamada a /api/empresas para toda a app -- antes havia mais de
  // 10 componentes a repetir este pedido de forma independente.
  useEffect(() => {
    let cancelado = false;
    apiGet("/api/empresas")
      .then((lista) => { if (!cancelado) setEmpresas(lista || []); })
      .catch(() => { if (!cancelado) setEmpresas([]); })
      .finally(() => { if (!cancelado) setLoading(false); });
    return () => { cancelado = true; };
  }, []);

  // Check de integridade: só depois de a lista REAL de empresas chegar,
  // confirma que o NIF lido do sessionStorage (num refresh) ainda é uma
  // empresa a sério -- foi apagada/desativada entretanto, ou alguém editou
  // o sessionStorage à mão nas devtools com um NIF inventado. Se não bater
  // certo, cai em "Todas as Empresas" -- não força logout nem volta ao ecrã
  // de escolha a meio de uma sessão já em curso, o TrocarEmpresa resolve.
  useEffect(() => {
    if (loading || !empresa) return;
    const existe = empresas.some((e) => e.nif === empresa);
    if (!existe) {
      setEmpresaState("");
      try { sessionStorage.removeItem(SESSION_EMPRESA_KEY); } catch { /* ignore */ }
    }
  }, [loading, empresas, empresa]);

  const setEmpresa = useCallback((nif) => {
    const valor = nif || "";
    setEmpresaState(valor);
    try {
      sessionStorage.setItem(SESSION_EMPRESA_KEY, valor);
      localStorage.setItem(LAST_EMPRESA_KEY, valor);
    } catch { /* storage indisponível -- sem persistência, sem crash */ }
  }, []);

  return (
    <EmpresaCtx.Provider value={{ empresa, empresas, loading, setEmpresa }}>
      {children}
    </EmpresaCtx.Provider>
  );
}

export function useEmpresa() {
  const ctx = useContext(EmpresaCtx);
  if (!ctx) {
    throw new Error("useEmpresa() só pode ser usado dentro de <EmpresaProvider>.");
  }
  return ctx;
}
